<script setup lang="ts">
import { onMounted, reactive, computed, onUnmounted } from 'vue';
import BreakDetails from '@/components/BreakDetails.vue';
import { CoButton, CoFormItem, CoRadioGroup, CoRadio } from '@deputy/copilot2';
import TimePicker from './TimePicker.vue';
import Datepicker from 'vue3-datepicker';
import { getTranslations } from '@/lib/util/i18n';
import {
  secondsToDateObject,
  dateToSeconds,
  isoDateToSeconds,
  secondsToIsoDate,
} from '@/lib/util/dateTime';
import { useTimesheetStore, BreakType } from '@/stores/timesheets';
import { isBreakOverlapping } from '@/lib/breaks/isBreakOverlapping';
import { addBreak } from '@/lib/breaks/addBreak';
import { replaceBreak } from '@/lib/breaks/replaceBreak';
import {
  getUnpaidBreakTemplate,
  getPaidBreakTemplate,
} from '@/lib/breaks/breakTemplates';
import { editBreakState } from '@/lib/breaks/editBreakState';
import { Slots } from '@/lib/request/types/timesheet';
import cloneDeep from 'lodash/cloneDeep';

const timesheets = useTimesheetStore();

const allBreaks = timesheets.getBreaks();
const currentBreak = computed(() => allBreaks[editBreakState.breakId] || null);

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'edit-break-form',
});

interface EditBreakState {
  breakType: string;
  breaks: Slots[];
  break: BreakState;
}

interface BreakState {
  startTime: any;
  endTime: any;
  startDate: Date;
  endDate: Date;
}

const state = reactive<EditBreakState>({
  breakType: t('break-option', {
    breakName: getUnpaidBreakTemplate().SlotName,
    breakPayType: getBreakPayType(getUnpaidBreakTemplate().SlotValue),
  }),
  breaks: cloneDeep(timesheets.getBreaks()),
  break: {
    startTime: {},
    endTime: {},
    startDate: new Date(),
    endDate: new Date(),
  },
});

function getBreakPayType(breakValue: string) {
  return breakValue === BreakType.Unpaid ? '(unpaid)' : '(paid)';
}

function showEditBreakForm(breakId: number) {
  emit('updateBreakModalTitle', t('title-edit'));
  editBreakState.editBreakForm = true;
  editBreakState.breakId = breakId;

  const startTimeObject = secondsToDateObject(
    timesheets.getStartBreakTime(editBreakState.breakId)
  );
  const endTimeObject = secondsToDateObject(
    timesheets.getEndBreakTime(editBreakState.breakId)
  );

  state.break.startTime = {
    hh: startTimeObject.hour,
    mm: startTimeObject.minute,
    a: startTimeObject.period.toLowerCase(),
  };

  state.break.endTime = {
    hh: endTimeObject.hour,
    mm: endTimeObject.minute,
    a: endTimeObject.period.toLowerCase(),
  };

  state.break.startDate = new Date(
    secondsToIsoDate(timesheets.getStartBreakTime(editBreakState.breakId))
  );
  state.break.endDate = new Date(
    secondsToIsoDate(timesheets.getEndBreakTime(editBreakState.breakId))
  );
}

function formatStartTime() {
  return (
    dateToSeconds(
      state.break.startDate.getFullYear(),
      state.break.startDate.getMonth() + 1,
      state.break.startDate.getDate(),
      state.break.startTime.data.HH,
      state.break.startTime.data.mm
    ) - isoDateToSeconds(timesheets.getStartShiftTime())
  );
}

function formatEndTime() {
  const duration =
    dateToSeconds(
      state.break.endDate.getFullYear(),
      state.break.endDate.getMonth() + 1,
      state.break.endDate.getDate(),
      state.break.endTime.data.HH,
      state.break.endTime.data.mm
    ) -
    dateToSeconds(
      state.break.startDate.getFullYear(),
      state.break.startDate.getMonth() + 1,
      state.break.startDate.getDate(),
      state.break.startTime.data.HH,
      state.break.startTime.data.mm
    );

  return formatStartTime() + duration;
}

function deleteBreak(breakId: number) {
  state.breaks.splice(breakId, 1);

  return state.breaks;
}

onMounted(() => {
  const modalTitle = state.breaks.length ? t('title-breaks') : '';
  emit('updateBreakModalTitle', modalTitle);
});

onUnmounted(() => {
  editBreakState.breakId = null;
  editBreakState.addBreakForm = false;
  editBreakState.editBreakForm = false;
});

const emit = defineEmits<{
  (e: 'updateBreak', breaksSlot?: Slots[]): void;
  (e: 'updateBreakModalTitle', step: string): void;
}>();
</script>

<template>
  <div class="edit-break">
    <div
      v-if="!editBreakState.editBreakForm && !editBreakState.addBreakForm"
      class="__breaks-list"
    >
      <BreakDetails
        :editBreaksModal="true"
        @showEditBreakForm="showEditBreakForm"
      ></BreakDetails>
      <CoButton
        @click="
          emit('updateBreakModalTitle', t('add-break'));
          editBreakState.addBreakForm = true;
          editBreakState.editBreakForm = false;
        "
        type="primary"
        size="large"
        >{{ t('add-break') }}</CoButton
      >
    </div>
    <div class="__breaks-form" v-else>
      <CoFormItem
        v-if="editBreakState.addBreakForm"
        :label="t('label-type')"
        label-position="left"
      >
        <CoRadioGroup v-model="state.breakType">
          <CoRadio
            :label="
              t('break-option', {
                breakName: getUnpaidBreakTemplate().SlotName,
                breakPayType: getBreakPayType(
                  getUnpaidBreakTemplate().SlotValue
                ),
              })
            "
            type="pill"
            size="large"
          ></CoRadio>
          <CoRadio
            :label="
              t('break-option', {
                breakName: getPaidBreakTemplate().SlotName,
                breakPayType: getBreakPayType(getPaidBreakTemplate().SlotValue),
              })
            "
            type="pill"
            size="large"
          ></CoRadio>
        </CoRadioGroup>
      </CoFormItem>
      <TimePicker
        v-model="state.break.startTime"
        :label="t('label-start')"
        :placeholder="t('placeholder-start')"
        ><div class="date-picker">
          <Datepicker
            inputFormat="dd MMM yyyy"
            v-model="state.break.startDate"
            :upperLimit="new Date()"
            :lowerLimit="new Date(timesheets.getStartShiftTime())"
          /></div
      ></TimePicker>
      <TimePicker
        v-model="state.break.endTime"
        :label="t('label-end')"
        :placeholder="t('placeholder-end')"
        ><div class="date-picker">
          <Datepicker
            inputFormat="dd MMM yyyy"
            v-model="state.break.endDate"
            :upperLimit="new Date()"
            :lowerLimit="new Date(timesheets.getStartShiftTime())"
          /></div
      ></TimePicker>
      <div
        class="__overlap-warning"
        v-if="isBreakOverlapping(currentBreak, allBreaks, state)"
      >
        {{ t('overlap-warning') }}
      </div>
      <div class="buttons">
        <CoButton
          data-test-id="delete"
          v-if="editBreakState.editBreakForm"
          @click="emit('updateBreak', deleteBreak(editBreakState.breakId))"
          type="destructive"
          size="large"
          >{{ t('delete') }}</CoButton
        >
        <CoButton
          data-test-id="save-add-break"
          @click="
            emit(
              'updateBreak',
              editBreakState.editBreakForm
                ? replaceBreak(state, formatEndTime(), formatStartTime())
                : addBreak(state, formatEndTime(), formatStartTime())
            )
          "
          :disabled="
            isBreakOverlapping(currentBreak, allBreaks, state) ||
            editBreakState.emptyField
          "
          type="primary"
          size="large"
          >{{ editBreakState.editBreakForm ? t('save') : t('add') }}</CoButton
        >
      </div>
    </div>
  </div>
</template>

<style scoped>
.co-button {
  width: 100%;
  margin-top: 32px;
}

.buttons {
  display: flex;
}
.buttons .co-button {
  margin-top: 0;
}

.co-button + .co-button {
  margin-left: 40px;
}
.__overlap-warning {
  margin-bottom: 8px;
  text-align: center;
  color: var(--co-text-attention);
}
</style>

<style>
.edit-break .co-form-item .co-form-item__label {
  font-weight: 500;
  font-size: 16px;
  color: var(--co-text-secondary);
}
.edit-break:focus-within > .co-form-item__label {
  color: var(--co-text-highlight);
}
.edit-break .co-form-item {
  display: flex;
  align-items: center;
}
.edit-break .co-form-item__content {
  width: 80%;
  margin-left: 0 !important;
}
.edit-break .co-radio--pill--large .co-radio--pill__inner {
  display: flex;
}
.edit-break .co-radio--pill .co-radio--pill__label {
  margin: auto;
}
.edit-break .co-radio-group {
  display: flex;
  width: 100%;
}
.edit-break .co-radio--pill {
  width: 50%;
  margin: 0 0 0 16px;
}
.edit-break .co-radio--pill__input,
.edit-break .co-radio--pill__inner {
  width: 100%;
}
</style>
